import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Button, LinkButton } from "../../../../../../components/Button";
import ConfirmModal from "../../../../../../components/ConfirmModal";
import * as Fonts from "../../../../../../components/Fonts";
import Input from "../../../../../../components/Form/Input/input";
import { FormGroup } from "../../../../../../components/Form/styles";
import { SubHeader } from "../../../../../../components/SubHeader";
import { whatsappDigits } from "../../../../../Contacts/helpers";
import WhatsappIcon from "../../../Sections/icons/Whatsapp";
import { navigationState } from "../../../Sections/navigationState";
import StorageIntegrationServiceInfo from "../../components/StorageIntegrationServiceInfo";
import { FormNavigationState } from "../../formNavigationState";
import { Card, CardFooter, Container, RemoveLink, Section } from "../styles";

const WhatsappLogo = () => <WhatsappIcon width={44} height={44} color="white" />;

/** A number WhatsApp can reach (the API's FormWhatsAppNotice::phone() takes the same ones). */
export const isWhatsappValid = (value: string) => whatsappDigits(value) !== null;

/**
 * The form's WhatsApp: each contact that arrives is told to this number, with a button that opens it in Mensagens
 * (sent by the API: FormWhatsAppNotice). Like the e-mail, it's stored when the form is saved.
 */
const WhatsappStoragePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isConfirmingRemove, setConfirmingRemove] = useState(false);

  const formNavigationState = navigationState.get() as FormNavigationState;
  const section = formNavigationState.section;
  const whatsapp = section.storage.whatsapp ?? { active: false };

  const saved = whatsapp.value || "";
  const [number, setNumber] = useState(saved);
  const hasUnsavedChanges = saved !== number;
  // A new valid number, or the first one when it's off
  const canSave = isWhatsappValid(number) && (hasUnsavedChanges || !whatsapp.active);

  const prefix = "pages.editor.form.form.storage";

  const updateSectionAndBack = (active: boolean, value?: string) => {
    const state: FormNavigationState = {
      ...formNavigationState,
      section: { ...section, storage: { ...section.storage, whatsapp: { active, value } } },
    };
    navigationState.set(state);
    navigate(-1);
  };

  const save = () => canSave && updateSectionAndBack(true, number.trim());

  return (
    <Container>
      <SubHeader
        title="WhatsApp"
        crumbs={[
          { label: t("pages.editor.preview.footer.actions.sections"), to: "/editor/sections" },
          { label: t("pages.editor.form.header.title"), to: "/editor/form" },
        ]}
        hasUnsavedChanges={hasUnsavedChanges}
      />

      <Section>
        <StorageIntegrationServiceInfo
          name="WhatsApp"
          LogoIcon={WhatsappLogo}
          logoContainerColor="#25d366"
          info={t(`${prefix}.whatsapp.info`)}
          active={whatsapp.active}
          statusLabel={whatsapp.active ? t(`${prefix}.status.active`) : t(`${prefix}.status.inactive`)}
        />
      </Section>

      <Section>
        <Fonts.Label>{t(`${prefix}.sections.setup`)}</Fonts.Label>
        <Card
          as="form"
          onSubmit={(e: React.FormEvent) => {
            e.preventDefault();
            save();
          }}>
          <FormGroup>
            <Input
              id="whatsappNumber"
              type="tel"
              value={number}
              label={t(`${prefix}.whatsapp.form.number.label`)}
              placeholder={t(`${prefix}.whatsapp.form.number.placeholder`)}
              onChange={e => setNumber(e.target.value)}
            />
          </FormGroup>
          <CardFooter>
            {hasUnsavedChanges && (
              <LinkButton onClick={() => setNumber(saved)}>{t("pages.settings.unsavedBar.discard")}</LinkButton>
            )}
            <Button type="INFO" size="MD" disabled={!canSave} onClick={save}>
              {t("actions.save")}
            </Button>
          </CardFooter>
        </Card>
      </Section>

      {whatsapp.active && (
        <RemoveLink type="button" onClick={() => setConfirmingRemove(true)}>
          {t(`${prefix}.whatsapp.actions.remove.link`)}
        </RemoveLink>
      )}

      {isConfirmingRemove && (
        <ConfirmModal
          type="DANGER"
          title={t(`${prefix}.whatsapp.actions.remove.confirmModal.title`)}
          cancelTitle={t(`${prefix}.email.actions.remove.confirmModal.cancelButton`)}
          confirmButtonTitle={t(`${prefix}.whatsapp.actions.remove.confirmModal.confirmButton`)}
          onConfirm={() => updateSectionAndBack(false)}
          onCancel={() => setConfirmingRemove(false)}
        />
      )}
    </Container>
  );
};

export default WhatsappStoragePage;
